/**
 * KAN-776: выбор архивов продавца: поле файла (несколько файлов) и зона, куда
 * архивы можно перетащить. Один файл - одиночный импорт, два и более - партия
 * (решает ImportArchiveForm по длине списка).
 */
import { useState, type DragEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export const ARCHIVE_LABEL = 'Архив zip от продавца';
export const ARCHIVE_HINT =
  'Внутри может быть папка tdata из Telegram Desktop или файлы .session и .json. Формат определится сам.';
export const DROP_HINT = 'Перетащите сюда архивы: один или сразу несколько';

interface Props {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function ArchiveDropZone({ onFiles, disabled }: Props): JSX.Element {
  const [over, setOver] = useState(false);

  const stop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      data-testid="archive-drop-zone"
      className={cn(
        'space-y-2 rounded-md border border-dashed p-3 transition-colors',
        over ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
      )}
      onDragEnter={(e) => {
        stop(e);
        if (!disabled) setOver(true);
      }}
      onDragOver={(e) => {
        stop(e);
        if (!disabled) setOver(true);
      }}
      onDragLeave={(e) => {
        stop(e);
        setOver(false);
      }}
      onDrop={(e) => {
        stop(e);
        setOver(false);
        if (disabled) return;
        const files = Array.from(e.dataTransfer?.files ?? []);
        if (files.length) onFiles(files);
      }}
    >
      <Label htmlFor="archive-zip">{ARCHIVE_LABEL}</Label>
      <Input
        id="archive-zip"
        type="file"
        multiple
        accept=".zip,application/zip"
        data-testid="archive-zip-input"
        aria-label={ARCHIVE_LABEL}
        aria-describedby="archive-zip-hint"
        disabled={disabled}
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          // тот же набор файлов можно выбрать повторно
          e.target.value = '';
        }}
      />
      <p id="archive-zip-hint" className="text-xs text-gray-500">
        {ARCHIVE_HINT}
      </p>
      <p className="text-xs text-gray-400" data-testid="archive-drop-hint">
        {DROP_HINT}
      </p>
    </div>
  );
}
